import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";

export const AdminCreateExercise = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  // State variables for exercise fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [video_url, setVideoUrl] = useState("");
  const [type, setType] = useState(""); // Dropdown selection
  const [tags, setTags] = useState(""); // Comma-separated tags

  const [types, setTypes] = useState([]); // Fetched types
  const [newType, setNewType] = useState(""); // Custom type input
  const [isAddingNewType, setIsAddingNewType] = useState(false); // Toggle input field

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch existing types from backend
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await fetch(API_URL.GET_EXERCISE_LIBRARY_TYPES, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        const data = await response.json();
        if (response.ok) {
          setTypes(data.types || []); // Ensure it's an array
        } else {
          throw new Error(data.error || "Failed to fetch exercise types");
        }
      } catch (err) {
        console.error("Error fetching types:", err);
      }
    };
    fetchTypes();
  }, [staff.token]);

  // Handle type selection
  const handleTypeChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "new") {
      setIsAddingNewType(true);
      setNewType("");
    } else {
      setType(selectedValue);
      setIsAddingNewType(false);
    }
  };

  // Handle adding a new type
  const handleAddNewType = () => {
    if (newType.trim() === "") return;

    // Add new type to list and select it
    setTypes((prev) => [...prev, newType]);
    setType(newType);
    setIsAddingNewType(false);
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    // Prepare payload
    const newExercise = {
      title,
      description,
      video_url,
      type,
      tags: tags.split(",").map((tag) => tag.trim()), // Convert to array
    };

    try {
      const response = await fetch(API_URL.POST_CREATE_EXERCISE_FROM_ADMIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(newExercise),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create exercise");
      }

      setSuccessMessage("Exercise created successfully!");
      setTimeout(() => {
        navigate("/administrator/exercise-library/manage");
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      {/* Main Content */}
      <div className="max-md:w-full md:flex-1 bg-bgBase overflow-auto p-5 md:p-10">
        <div className="flex flex-col justify-between gap-5 mb-6">
          {/* Go Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:ml-12"
          >
            <GoArrowLeft color="#666666" /> Back To Library
          </button>
          <h1 className="font-inter font-medium text-dark text-3xl !leading-none">
            Create Exercise
          </h1>
        </div>

        {/* Form Section */}
        <form
          onSubmit={handleSubmit}
          className="space-y-3 md:space-y-6 bg-white border border-tblBorder p-5 md:p-8 rounded-14"
        >
          {/* Title Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Title*
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exercise title"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
              required
            />
          </div>

          {/* Description Input (TextArea) */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Description*
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter exercise description"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
              required
            />
          </div>

          {/* Video URL Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              YouTube Video URL
            </label>
            <input
              type="text"
              value={video_url}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Enter YouTube embed URL"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
            />
          </div>

          {/* Type Dropdown & New Type Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Type*
            </label>
            {!isAddingNewType ? (
              <select
                value={type}
                onChange={handleTypeChange}
                className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                required
              >
                <option value="">Select Type</option>
                {types.map((t, index) => (
                  <option key={index} value={t}>
                    {t}
                  </option>
                ))}
                <option value="new">➕ Add New Type</option>
              </select>
            ) : (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  placeholder="Enter new type"
                  className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                />
                <button
                  type="button"
                  onClick={handleAddNewType}
                  className="px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  ✅
                </button>
              </div>
            )}
          </div>

          {/* Tags Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. agility, endurance, core"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 p-3 rounded-10 my-3">
              {" "}
              <p className="text-red-500 mb-4 font-inter font-medium text-sm">
                {error}
              </p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-600/10 p-3 rounded-10 my-3">
              <p className="text-green-600 font-inter font-medium text-sm">
                {successMessage}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={isLoading}
              className={`${
                isLoading ? "bg-dark/70" : " hover:hover:bg-black"
              } px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black`}
            >
              {isLoading ? "Creating..." : "Create Exercise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
